import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { PanelRightOpen } from "lucide-react";
import type { BoardBackground, BoardMeta } from "../../shared/model";
import type { Editor } from "../editor/editor";
import { Canvas } from "../editor/react/Canvas";
import { EditorContext, useCamera, useEditor, useUi } from "../editor/react/hooks";
import type { LaserTool } from "../editor/tools/misc";
import type { BoardSync } from "../sync/sync";
import { IconButton, useToast } from "../ui/kit";
import { ActionBar, TitleCard, ZoomBar, type MenuItem, type SyncState } from "./TopBar";
import { Toolbar } from "./Toolbar";
import { ToolOptions } from "./ToolOptions";
import { Inspector } from "./Inspector";
import { StickerDrawer } from "./StickerDrawer";
import { ContextMenu } from "./ContextMenu";
import { BackgroundDialog, ExportDialog, PagesDialog, SettingsDialog, ShareDialog, ShortcutsDialog } from "./dialogs";
import { Present } from "./Present";
import { LayersPanel } from "./LayersPanel";
import { attachBoardIo, insertImages } from "./io";

export interface BoardViewProps {
  editor: Editor;
  sync: BoardSync;
  meta: BoardMeta;
  kind: "cloud" | "guest" | "shared";
  onTitle: (t: string) => void;
  onBackground: (bg: BoardBackground) => void;
  onBack: () => void;
  backLabel: string;
  onMetaChange: (m: Partial<BoardMeta>) => void;
  extraMenu?: MenuItem[];
  banner?: React.ReactNode;
}

function useSyncState(sync: BoardSync): SyncState {
  const sub = useCallback((fn: () => void) => sync.subscribe(fn), [sync]);
  return useSyncExternalStore(sub, () => sync.state, () => sync.state);
}

const PRESENCE_COLORS = ["#c2583a", "#3f64a0", "#4f8a5b", "#7d4a86", "#cf9234", "#2f7c79"];

function PresenceLayer({ sync }: { sync: BoardSync }) {
  const sub = useCallback((fn: () => void) => sync.subscribePresence(fn), [sync]);
  const version = useSyncExternalStore(sub, () => sync.presence, () => sync.presence);
  const cam = useCamera((c) => ({ x: c.x, y: c.y, z: c.z }));
  const [, tick] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => tick((n) => n + 1), 2000);
    return () => window.clearInterval(t);
  }, []);
  const now = Date.now();
  const people = [...version.values()].filter((p) => now - p.seen < 12000);
  if (!people.length) return null;
  return (
    <svg className="presence-layer" aria-hidden="true">
      {people.map((p, i) => {
        const color = PRESENCE_COLORS[i % PRESENCE_COLORS.length];
        const sx = p.x * cam.z + cam.x;
        const sy = p.y * cam.z + cam.y;
        return (
          <g key={p.cid}>
            {p.laser && p.laser.length > 1 && (
              <polyline className="laser-core" points={p.laser.map(([x, y]) => `${x * cam.z + cam.x},${y * cam.z + cam.y}`).join(" ")} />
            )}
            <g transform={`translate(${sx} ${sy})`}>
              <path d="M0 0 L5 16 L8 9 L15 6 Z" fill={color} stroke="#fbf7ef" strokeWidth="1.4" strokeLinejoin="round" />
              <text x="14" y="26" className="presence-name" fill={color}>
                {p.name}
              </text>
            </g>
          </g>
        );
      })}
    </svg>
  );
}

function PresenceSender({ sync }: { sync: BoardSync }) {
  const ed = useEditor();
  useEffect(() => {
    const vp = ed.viewport;
    if (!vp) return;
    const onMove = (e: PointerEvent) => {
      const r = vp.getBoundingClientRect();
      const w = ed.camera.toWorld({ x: e.clientX - r.left, y: e.clientY - r.top });
      sync.sendPresence({ x: w.x, y: w.y, tool: ed.ui.tool, color: ed.style.inkColor });
    };
    vp.addEventListener("pointermove", onMove, { passive: true });
    const laser = ed.tools.get("laser") as LaserTool | undefined;
    if (laser)
      laser.onTrail = (pts) => {
        const last = pts[pts.length - 1];
        if (last) sync.sendPresence({ x: last.x, y: last.y, tool: "laser", color: "#ff6a3d", laser: pts.map((p) => [Math.round(p.x), Math.round(p.y)] as [number, number]) });
      };
    return () => {
      vp.removeEventListener("pointermove", onMove);
      if (laser) laser.onTrail = undefined;
    };
  }, [ed, sync, ed.viewport]);
  return null;
}

function Chrome(props: BoardViewProps & { sync: BoardSync }) {
  const ed = useEditor();
  const toast = useToast();
  const syncState = useSyncState(props.sync);
  const readOnly = useUi((ui) => ui.readOnly);
  const tool = useUi((ui) => ui.tool);
  const leftHanded = useUi((_, e) => e.settings.leftHanded);
  const hasSelection = useUi((ui) => ui.selection.length > 0 && !ui.busy);
  // typing with the keyboard keeps the page clear: the options bar already has font, size and colour
  const typing = useUi((_, e) => e.keyboardMode);
  const [dialog, setDialog] = useState<null | "export" | "share" | "bg" | "settings" | "help" | "pages">(null);
  const [stickers, setStickers] = useState(false);
  const [presenting, setPresentingState] = useState(false);
  const setPresenting = (on: boolean) => {
    // fullscreen must be requested from the tap itself
    if (on) document.documentElement.requestFullscreen?.().catch(() => undefined);
    setPresentingState(on);
  };
  const [inspectorOpen, setInspectorOpen] = useState(() => localStorage.getItem("sepia.inspector") !== "0");
  const [layersOpen, setLayersOpenState] = useState(() => localStorage.getItem("sepia.layers") === "1");
  const setLayersOpen = (v: boolean) => {
    setLayersOpenState(v);
    localStorage.setItem("sepia.layers", v ? "1" : "0");
  };
  const fileRef = useRef<HTMLInputElement>(null);
  const side = leftHanded ? "right" : "left";
  const other = leftHanded ? "left" : "right";

  useEffect(() => {
    if (tool === "sticker") setStickers(true);
  }, [tool]);

  useEffect(
    () =>
      attachBoardIo(ed, {
        onImagePicker: () => fileRef.current?.click(),
        onOpenStickers: () => setStickers(true),
        onPresent: () => setPresenting(true),
        onHelp: () => setDialog("help"),
        onExport: () => setDialog("export"),
        onNewLayer: () => {
          ed.addLayer();
          setLayersOpen(true);
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ed]
  );

  const menu: MenuItem[] = useMemo(
    () => [
      { label: "Paper & grid", onClick: () => setDialog("bg") },
      { label: "Pencil & gestures", onClick: () => setDialog("settings") },
      { label: "Shortcuts & gestures", hint: "?", onClick: () => setDialog("help") },
      { divider: true, label: "", onClick: () => undefined },
      ...(readOnly ? [] : [{ label: "Insert image", hint: "I", onClick: () => fileRef.current?.click() }]),
      { label: "Select all", hint: "⌘A", onClick: () => ed.selectAll() },
      { label: "Fit everything", hint: "⇧1", onClick: () => ed.zoomToFit() },
      ...(props.extraMenu ?? [])
    ],
    [ed, readOnly, props.extraMenu]
  );

  const setInspector = (v: boolean) => {
    setInspectorOpen(v);
    localStorage.setItem("sepia.inspector", v ? "1" : "0");
  };

  if (presenting) {
    return (
      <>
        <Present onExit={() => setPresenting(false)} />
        <PresenceSender sync={props.sync} />
      </>
    );
  }

  return (
    <>
      <div className="chrome-top">
        <TitleCard title={props.meta.title} onTitle={props.onTitle} sync={syncState} onBack={props.onBack} backLabel={props.backLabel} editable={!readOnly && props.kind !== "shared"} />
        <ActionBar
          onShare={() => setDialog("share")}
          onExport={() => setDialog("export")}
          onPresent={() => setPresenting(true)}
          onPages={() => setDialog("pages")}
          onLayers={() => setLayersOpen(!layersOpen)}
          layersOpen={layersOpen}
          menu={menu}
          canShare={props.kind === "cloud"}
        />
      </div>
      {props.banner}
      <Toolbar side={side} onImage={() => fileRef.current?.click()} />
      {stickers && !readOnly && (
        <StickerDrawer
          side={side}
          onClose={() => {
            setStickers(false);
            if (ed.ui.tool === "sticker") ed.setTool("select");
          }}
        />
      )}
      {!readOnly && <ToolOptions onOpenStickers={() => setStickers(true)} onOpenLayers={() => setLayersOpen(true)} />}
      {layersOpen && <LayersPanel onClose={() => setLayersOpen(false)} />}
      {typing ? null : inspectorOpen ? (
        <Inspector side={other} onClose={() => setInspector(false)} />
      ) : (
        hasSelection && !readOnly && (
          <div className={"inspector-tab inspector-tab-" + other}>
            <IconButton icon={PanelRightOpen} label="Show selection panel" onClick={() => setInspector(true)} />
          </div>
        )
      )}
      <ZoomBar onBackground={() => setDialog("bg")} onPages={() => setDialog("pages")} />
      <ContextMenu
        onPasteRequest={async () => {
          try {
            const text = await navigator.clipboard.readText();
            const els = ed.parseClipboardText(text);
            if (els) ed.pasteElements(els);
            else toast("Use ⌘V (or the keyboard's paste) to paste here.");
          } catch {
            ed.pasteElements();
          }
        }}
      />
      <PresenceSender sync={props.sync} />
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          const files = [...(e.target.files ?? [])];
          e.target.value = "";
          if (files.length) insertImages(ed, files);
        }}
      />
      <ExportDialog open={dialog === "export"} onClose={() => setDialog(null)} title={props.meta.title} bg={props.meta.bg} />
      <BackgroundDialog open={dialog === "bg"} onClose={() => setDialog(null)} bg={props.meta.bg} onChange={props.onBackground} />
      <SettingsDialog open={dialog === "settings"} onClose={() => setDialog(null)} />
      <ShortcutsDialog open={dialog === "help"} onClose={() => setDialog(null)} />
      <PagesDialog open={dialog === "pages"} onClose={() => setDialog(null)} boardId={props.kind === "cloud" ? props.meta.id : undefined} />
      {props.kind === "cloud" && <ShareDialog open={dialog === "share"} onClose={() => setDialog(null)} board={props.meta} onChange={props.onMetaChange} />}
    </>
  );
}

export function BoardView(props: BoardViewProps) {
  return (
    <EditorContext.Provider value={props.editor}>
      <div className="board">
        <Canvas>
          <PresenceLayer sync={props.sync} />
        </Canvas>
        <Chrome {...props} />
      </div>
    </EditorContext.Provider>
  );
}
